import { relatoryGet } from 'api/Assessor/methods';
import {
  AssemblyModalities,
  AssemblyType,
  Toggle,
} from 'helpers/enums/Assembleia';
import { toRequest } from 'helpers/utils/RequestUtils';
import {
  getAssembleiasAssessorBffEndpoint,
  getAssembleiasAssessorEndpoint,
} from 'library/Apis';
import ApiService from 'services/ApiService';
import { getMergedParamsFromObject } from '../helpers/utils/RequestUtils';

type postRegisterPresenceProps = {
  convocacoes: Array<number>;
  cpf: string;
  idEvento: number;
  tipoAssinatura: 'BIOMETRIA' | 'FISICA' | null;
  metadados?: {
    latitude?: number;
    longitude?: number;
    data?: string;
    userAgent?: string;
    imagem?: string | undefined;
    ip?: string;
  };
};

type PresenceProps = {
  idPauta: number;
  agencia?: string;
  nucleo?: string;
};

type postReorderAgendaProps = {
  eventId: number;
  agendaIds: number[];
};

type postDataStepProps = {
  eventId?: number;
  title: string;
  cooperative?: string;
  type: AssemblyType;
  modality: AssemblyModalities;
  description: string;
  link: string;
  quantityAssociates?: any;
  microsoftTeams?: Toggle;
  startDate: string;
  endDate: string;
  hybridStartDate: string;
  hybridEndDate: string;
};

type postEntitieStepProps = {
  eventId: number;
  selectedNucleuses: {
    [key: string]: number[];
  };
};

type postLocalStepProps = {
  eventId: number;
  postcode: string;
  state: string;
  city: string;
  neighborhood: string;
  address: string;
  number: string;
  localReference: string;
};

type PresenceAgenciaProps = {
  idPauta: number;
};

type PresenceNucleoProps = {
  agencia: string;
  idPauta: number;
};

type CheckAvailabilityProps = {
  agencia: string;
  idPauta: number;
  nucleo: string;
};

type InPersonVoteProps = {
  agencia: string;
  flagSemVotos: boolean;
  idPauta: number;
  itens: [
    {
      flagAclamacao: boolean;
      idItemPauta: number;
      quantidade: number;
    }
  ];
  nucleo: string;
};

type AllowAvailabilityProps = {
  agencia: string;
  idPauta: number;
  nucleo: string;
};

export type RegisterAcclaimProps = {
  idPauta: number;
  agencia: string;
  itens: { idItemPauta: number; flagAclamacao: boolean }[];
};

function isNumeric(str: string) {
  return /^\d+$/.test(str);
}

export const getAccountsToConfirm = (
  eventId: number,
  value: string,
  isSearchByCpf: boolean
) => {
  const url = `${getAssembleiasAssessorEndpoint(
    '1'
  )}evento/${eventId}/convocado/${
    isNumeric(value.replace(/[^\w\s]/gi, '')) ? value.replace(/\D/g, '') : value
  }/${isSearchByCpf}`;
  return toRequest(ApiService.get, [url], 'getAccountsToConfirm');
};

export const postRegisterPresence = (request: postRegisterPresenceProps) => {
  const url = `${getAssembleiasAssessorEndpoint('1')}presenca/`;
  return toRequest(ApiService.post, [url, request], 'postRegisterPresence');
};

export const getPresence = (request: PresenceProps) => {
  const url = `${getAssembleiasAssessorEndpoint(
    '1'
  )}presenca/presencial?${getMergedParamsFromObject(request)}`;
  return toRequest(ApiService.get, [url]);
};

export const getRetroactiveDay = () => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}event/limit-day-to-retroactive`;
  return toRequest(ApiService.get, [url]);
};

export const getAllEventStatus = () => {
  const url = `${getAssembleiasAssessorBffEndpoint('1')}event/all-event-status`;
  return toRequest(ApiService.get, [url]);
};

export const getAssemblyType = () => {
  const url = `${getAssembleiasAssessorBffEndpoint('1')}event/settings`;
  return toRequest(ApiService.get, [url]);
};

export const putSurvey = (
  eventId: number,
  cooperative?: string,
  templatePesquisa?: string
) => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}event/survey?${getMergedParamsFromObject({
    eventId,
    ...(templatePesquisa && {
      cooperative,
      'template-pesquisa': templatePesquisa,
    }),
  })}`;
  return toRequest(ApiService.put, [url]);
};

export const getSurvey = (eventId: number) => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}event/survey-preview?eventId=${eventId}`;
  return relatoryGet<ArrayBuffer>(url, {
    responseType: 'arraybuffer',
  });
};

export const postMakeEditable = (eventId: number) => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}event/make-editable?eventId=${eventId}`;
  return toRequest(ApiService.post, [url]);
};

export const getCooperative = () => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}organizational_structure/cooperative`;
  return toRequest(ApiService.get, [url]);
};

export const getTemplates = (coop: string, modeloPesquisa: string) => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}template-pesquisa?cooperativa=${coop}&modeloEvento=${modeloPesquisa}`;
  return toRequest(ApiService.get, [url]);
};

export const getAgencies = (coop: string) => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}organizational_structure/branch?cooperative=${coop}`;
  return toRequest(ApiService.get, [url]);
};

export const getNucleuses = (
  cooperative: string,
  branches: string[],
  config?: any
) => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}nucleus_setting/find?cooperative=${cooperative}${branches!
    ?.map((b: string) => `&branches=${b}`)
    .join('')}`;

  return toRequest(ApiService.get, [url, config], 'getList');
};

export const postDataStep = (request: postDataStepProps) => {
  const url = `${getAssembleiasAssessorBffEndpoint('1')}event/`;
  return toRequest(ApiService.post, [url, request], 'postList');
};

export const putDataStep = (request: postDataStepProps) => {
  const url = `${getAssembleiasAssessorBffEndpoint('1')}event/`;
  return toRequest(ApiService.put, [url, request], 'postList');
};

export const postEntitiesStep = (request: postEntitieStepProps) => {
  const url = `${getAssembleiasAssessorBffEndpoint('1')}nucleus_setting/`;
  return toRequest(ApiService.post, [url, request], 'postList');
};

export const postLocalStep = (request: postLocalStepProps) => {
  const url = `${getAssembleiasAssessorBffEndpoint('1')}address/`;
  return toRequest(ApiService.post, [url, request], 'postList');
};

export const saveStaves = (request: {
  eventId: number | undefined;
  targetEventId: number;
  agendaIds: number[];
}) => {
  const url = `${getAssembleiasAssessorBffEndpoint('1')}agenda/copy-agendas`;
  return toRequest(ApiService.post, [url, request], 'postList');
};

export const getAgenda = (eventId: number, page?: string, size?: string) => {
  const url = `${getAssembleiasAssessorEndpoint(
    '1'
  )}pauta?idEvento=${eventId}&page=${page ? page : '0'}&size=${
    size ? size : '500'
  }`;
  return toRequest(ApiService.get, [url], 'getAgenda');
};

export const filterStavesByName = (props: {
  eventId: number;
  text: string;
}) => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}event/search?${getMergedParamsFromObject(props)}`;
  return toRequest(ApiService.get, [url], 'filterStavesByName', false);
};

export const getStavesById = (eventId: number) => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}agenda/search?eventId=${eventId}`;
  return toRequest(ApiService.get, [url], 'getAgenda');
};

// searchStaves: (SearchProps: { coop: number | string; text: string }) => {
//     return get<TipoPautaResponse[]>(
//       'assembleiasAssessor',
//       `pauta/search?${getMergedParamsFromObject(SearchProps)}`
//     );
//   },

export const getAgendaRegister = (eventId: number) => {
  const url = `${getAssembleiasAssessorBffEndpoint(
    '1'
  )}agenda/?eventId=${eventId}`;
  return toRequest(ApiService.get, [url], 'getAgenda');
};

export const postReorderAgenda = (request: postReorderAgendaProps) => {
  const url = `${getAssembleiasAssessorEndpoint('1')}pauta/ordenar`;
  return toRequest(ApiService.post, [url, request], 'postReorderAgenda');
};

export const postStaveOrder = (request: postReorderAgendaProps) => {
  const url = `${getAssembleiasAssessorBffEndpoint('1')}agenda/sort`;
  return toRequest(ApiService.post, [url, request], 'postReorderAgenda');
};

export const getPresenceAgencia = (request: PresenceAgenciaProps) => {
  const url = `${getAssembleiasAssessorEndpoint(
    '1'
  )}presenca/presencial/agencia?${getMergedParamsFromObject(request)}`;
  return toRequest(ApiService.get, [url], 'getAgencia');
};

export const getPresenceNucleo = (request: PresenceNucleoProps) => {
  const url = `${getAssembleiasAssessorEndpoint(
    '1'
  )}presenca/presencial/nucleo?${getMergedParamsFromObject(request)}`;
  return toRequest(ApiService.get, [url], 'getNucleo');
};

export const checkAvailability = (request: CheckAvailabilityProps) => {
  const url = `${getAssembleiasAssessorEndpoint(
    '1'
  )}voto-presencial/disponibilidade?${getMergedParamsFromObject(request)}`;
  return toRequest(ApiService.post, [url], 'checkAvailability');
};

export const inPersonVote = (request: InPersonVoteProps) => {
  const url = `${getAssembleiasAssessorEndpoint(
    '1'
  )}voto-presencial/simultaneo`;
  return toRequest(ApiService.post, [url, request], 'inPersonVote');
};

export const registerAcclaim = (request: RegisterAcclaimProps) => {
  const url = `${getAssembleiasAssessorEndpoint(
    '1'
  )}voto-presencial/simultaneo/aclamacao`;
  return toRequest(ApiService.post, [url, request], 'registerAcclaim');
};

export const allowAvailability = (request: AllowAvailabilityProps) => {
  const url = `${getAssembleiasAssessorEndpoint(
    '1'
  )}voto-presencial/cancelar?${getMergedParamsFromObject(request)}`;
  return toRequest(ApiService.delete, [url], 'allowAvailability');
};
